import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, TrendingUp, Plus, Edit2, Trash2, LogOut, Eye, EyeOff, Shield, Upload, X, Image as ImageIcon, Save, Users, UserCheck, UserX, Mail, Copy, Link2, Clock, Megaphone, AlertTriangle, Ticket, Percent, DollarSign, Star, MessageSquare, CheckCircle, XCircle, FolderOpen } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin, signOut, getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    expires_at: '',
    is_active: true
  });
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [createdInviteLink, setCreatedInviteLink] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [promotionUploading, setPromotionUploading] = useState(false);
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [promotionImagePreview, setPromotionImagePreview] = useState(null);
  const [promotionForm, setPromotionForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    image_url: '',
    link_url: '',
    is_active: true
  });
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    category: '',
    stock: '',
    image_url: '',
    featured: false,
    is_active: true
  });

  // Category management state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const [categoryImagePreview, setCategoryImagePreview] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: ''
  });

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statsRes, productsRes, ordersRes, categoriesRes, promotionsRes, usersRes, invitesRes, discountsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }).catch((err) => {
          console.error('Stats fetch error:', err.response?.status, err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${API}/products?limit=100`),
        axios.get(`${API}/orders`, { headers }).catch((err) => {
          console.error('Orders fetch error:', err.response?.status);
          return { data: [] };
        }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/promotions?active_only=false`),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/invites`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/discounts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      // If stats API failed, calculate from available data
      const fetchedProducts = productsRes.data || [];
      const fetchedOrders = ordersRes.data || [];
      
      if (statsRes.data) {
        setStats(statsRes.data);
      } else {
        // Fallback: calculate stats from products and orders data
        const activeProducts = fetchedProducts.filter(p => p.is_active !== false);
        const pendingOrders = fetchedOrders.filter(o => o.status === 'pending');
        const completedOrders = fetchedOrders.filter(o => o.payment_status === 'completed');
        const totalRevenue = completedOrders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
        const lowStockThreshold = 10;
        const lowStockProducts = activeProducts.filter(p => (p.stock || 0) <= lowStockThreshold);
        
        setStats({
          total_products: fetchedProducts.length,
          active_products: activeProducts.length,
          total_orders: fetchedOrders.length,
          pending_orders: pendingOrders.length,
          total_revenue: totalRevenue,
          low_stock_products: lowStockProducts,
          low_stock_count: lowStockProducts.length,
          out_of_stock_count: activeProducts.filter(p => (p.stock || 0) === 0).length,
          low_stock_threshold: lowStockThreshold
        });
      }
      
      setProducts(fetchedProducts);
      setOrders(fetchedOrders);
      setCategories(categoriesRes.data);
      setPromotions(promotionsRes.data);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
      setDiscounts(discountsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, fetchData]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price: '',
      currency: 'USD',
      category: '',
      stock: '',
      image_url: '',
      featured: false,
      is_active: true
    });
    setEditingProduct(null);
    setImagePreview(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      };

      const response = await axios.post(`${API}/upload/image`, formData, { headers });
      setProductForm({ ...productForm, image_url: response.data.url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handlePromotionImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPromotionImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setPromotionUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      };

      const response = await axios.post(`${API}/upload/image`, formData, { headers });
      setPromotionForm({ ...promotionForm, image_url: response.data.url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setPromotionImagePreview(null);
    } finally {
      setPromotionUploading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock)
      };

      if (editingProduct) {
        const response = await axios.put(`${API}/products/${editingProduct.id}`, data, { headers });
        // Merge: original product + form data + any server response data
        const updatedProduct = { 
          ...editingProduct, 
          ...data, 
          ...(response.data || {}),
          id: editingProduct.id 
        };
        setProducts(prev => prev.map(p => p.id === editingProduct.id ? updatedProduct : p));
        toast.success('Product updated');
      } else {
        const response = await axios.post(`${API}/products`, data, { headers });
        // Add new product to local state
        setProducts(prev => [response.data, ...prev]);
        toast.success('Product created');
      }
      
      resetProductForm();
      setProductDialogOpen(false);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/products/${productId}`, { headers });
      // Remove from local state instead of refetching
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success('Product deleted');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      currency: product.currency || 'USD',
      category: product.category,
      stock: product.stock.toString(),
      image_url: product.image_url,
      featured: product.featured,
      is_active: product.is_active
    });
    setImagePreview(product.image_url);
    setProductDialogOpen(true);
  };

  const handleUpdateStock = async (productId, newStock) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/products/${productId}`, { stock: parseInt(newStock) }, { headers });
      // Update local state
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, stock: parseInt(newStock) } : p));
      toast.success('Stock updated');
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Failed to update stock');
    }
  };

  const handleOrderStatus = async (orderId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/orders/${orderId}/status?status=${newStatus}`, {}, { headers });
      // Update local state
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success('Order status updated');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const handleUserRoleUpdate = async (userId, newRole) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { headers });
      // Update local state
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success(`User ${newRole === 'admin' ? 'promoted to admin' : 'demoted to user'}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const handleCreateInvite = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/invites`, {
        email: newInviteEmail || null,
        role: 'admin'
      }, { headers });
      
      const inviteLink = `${window.location.origin}/signup?invite=${response.data.token}`;
      setCreatedInviteLink(inviteLink);
      setNewInviteEmail('');
      // Add to local state
      setInvites(prev => [response.data, ...prev]);
      toast.success('Invite created!');
    } catch (error) {
      console.error('Error creating invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  const handleCopyInviteLink = (token) => {
    const link = `${window.location.origin}/signup?invite=${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard!');
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/invites/${inviteId}`, { headers });
      // Remove from local state
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Invite revoked');
    } catch (error) {
      console.error('Error revoking invite:', error);
      toast.error('Failed to revoke invite');
    }
  };

  // Promotion management functions
  const resetPromotionForm = () => {
    setPromotionForm({
      title: '',
      subtitle: '',
      description: '',
      image_url: '',
      link_url: '',
      is_active: true
    });
    setEditingPromotion(null);
    setPromotionImagePreview(null);
  };

  const handleEditPromotion = (promo) => {
    setEditingPromotion(promo);
    setPromotionForm({
      title: promo.title || '',
      subtitle: promo.subtitle || '',
      description: promo.description || '',
      image_url: promo.image_url || '',
      link_url: promo.link_url || '',
      is_active: promo.is_active !== false
    });
    setPromotionImagePreview(promo.image_url);
    setPromotionDialogOpen(true);
  };

  const handlePromotionSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    
    try {
      if (editingPromotion) {
        const response = await axios.put(`${API}/promotions/${editingPromotion.id}`, promotionForm, { headers });
        setPromotions(prev => prev.map(p => p.id === editingPromotion.id ? response.data : p));
        toast.success('Promotion updated');
      } else {
        const response = await axios.post(`${API}/promotions`, promotionForm, { headers });
        setPromotions(prev => [response.data, ...prev]);
        toast.success('Promotion created');
      }
      
      resetPromotionForm();
      setPromotionDialogOpen(false);
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast.error(error.response?.data?.detail || 'Failed to save promotion');
    }
  };

  const handleDeletePromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/promotions/${promotionId}`, { headers });
      setPromotions(prev => prev.filter(p => p.id !== promotionId));
      toast.success('Promotion deleted');
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast.error('Failed to delete promotion');
    }
  };

  const handleTogglePromotionStatus = async (promo) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/promotions/${promo.id}`, {
        ...promo,
        is_active: !promo.is_active
      }, { headers });
      setPromotions(prev => prev.map(p => p.id === promo.id ? { ...p, is_active: !p.is_active } : p));
      toast.success(`Promotion ${promo.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error toggling promotion:', error);
      toast.error('Failed to update promotion');
    }
  };

  // Discount handlers
  const resetDiscountForm = () => {
    setDiscountForm({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_uses: '',
      expires_at: '',
      is_active: true
    });
    setEditingDiscount(null);
  };

  const handleEditDiscount = (discount) => {
    setEditingDiscount(discount);
    setDiscountForm({
      code: discount.code,
      description: discount.description || '',
      discount_type: discount.discount_type,
      discount_value: discount.discount_value.toString(),
      min_order_amount: discount.min_order_amount?.toString() || '',
      max_uses: discount.max_uses?.toString() || '',
      expires_at: discount.expires_at ? discount.expires_at.split('T')[0] : '',
      is_active: discount.is_active
    });
    setDiscountDialogOpen(true);
  };

  const handleDiscountSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    
    const data = {
      code: discountForm.code,
      description: discountForm.description || null,
      discount_type: discountForm.discount_type,
      discount_value: parseFloat(discountForm.discount_value),
      min_order_amount: discountForm.min_order_amount ? parseFloat(discountForm.min_order_amount) : null,
      max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
      expires_at: discountForm.expires_at ? new Date(discountForm.expires_at).toISOString() : null,
      is_active: discountForm.is_active
    };
    
    try {
      if (editingDiscount) {
        const response = await axios.put(`${API}/discounts/${editingDiscount.id}`, data, { headers });
        setDiscounts(prev => prev.map(d => d.id === editingDiscount.id ? response.data : d));
        toast.success('Discount updated');
      } else {
        const response = await axios.post(`${API}/discounts`, data, { headers });
        setDiscounts(prev => [response.data, ...prev]);
        toast.success('Discount created');
      }
      
      resetDiscountForm();
      setDiscountDialogOpen(false);
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error(error.response?.data?.detail || 'Failed to save discount');
    }
  };

  const handleDeleteDiscount = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/discounts/${discountId}`, { headers });
      setDiscounts(prev => prev.filter(d => d.id !== discountId));
      toast.success('Discount deleted');
    } catch (error) {
      console.error('Error deleting discount:', error);
      toast.error('Failed to delete discount');
    }
  };

  const handleToggleDiscountStatus = async (discount) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/discounts/${discount.id}`, {
        is_active: !discount.is_active
      }, { headers });
      setDiscounts(prev => prev.map(d => d.id === discount.id ? { ...d, is_active: !d.is_active } : d));
      toast.success(`Discount ${discount.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error toggling discount:', error);
      toast.error('Failed to update discount');
    }
  };

  // Review management handlers
  const handleApproveReview = async (reviewId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/reviews/${reviewId}`, { is_approved: true }, { headers });
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, is_approved: true } : r));
      toast.success('Review approved');
    } catch (error) {
      console.error('Error approving review:', error);
      toast.error('Failed to approve review');
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/reviews/${reviewId}`, { is_approved: false }, { headers });
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, is_approved: false } : r));
      toast.success('Review rejected');
    } catch (error) {
      console.error('Error rejecting review:', error);
      toast.error('Failed to reject review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/reviews/${reviewId}`, { headers });
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      toast.success('Review deleted');
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Unknown Product';
  };

  // Category management handlers
  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      slug: '',
      description: '',
      image_url: ''
    });
    setEditingCategory(null);
    setCategoryImagePreview(null);
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleCategoryNameChange = (name) => {
    setCategoryForm(prev => ({
      ...prev,
      name,
      slug: editingCategory ? prev.slug : generateSlug(name)
    }));
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, WebP, or GIF');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCategoryImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setCategoryImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      };

      const response = await axios.post(`${API}/upload/image`, formData, { headers });
      setCategoryForm(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setCategoryImagePreview(null);
    } finally {
      setCategoryImageUploading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image_url: category.image_url || ''
    });
    setCategoryImagePreview(category.image_url);
    setCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();

    try {
      if (editingCategory) {
        const response = await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, { headers });
        const updatedCategory = { ...editingCategory, ...categoryForm, ...(response.data || {}) };
        setCategories(prev => prev.map(c => c.id === editingCategory.id ? updatedCategory : c));
        toast.success('Category updated');
      } else {
        const response = await axios.post(`${API}/categories`, categoryForm, { headers });
        setCategories(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Category created');
      }

      resetCategoryForm();
      setCategoryDialogOpen(false);
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    const categoryInUse = products.some(p => p.category === categories.find(c => c.id === categoryId)?.slug);
    if (categoryInUse) {
      toast.error('Cannot delete category with existing products. Reassign products first.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/categories/${categoryId}`, { headers });
      setCategories(prev => prev.filter(c => c.id !== categoryId));
      toast.success('Category deleted');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const getProductCountForCategory = (slug) => {
    return products.filter(p => p.category === slug).length;
  };

  // Not authenticated
  if (!user) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-login-required">
        <div className="w-full max-w-md text-center p-8">
          <Shield className="w-16 h-16 text-[#C9A962] mx-auto mb-6" />
          <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">
            Admin Access Required
          </h1>
          <p className="text-[#737373] mb-8">
            Please sign in with an admin account to access the dashboard.
          </p>
          <Button 
            asChild
            className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 px-8"
          >
            <Link to="/login">Sign In</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-access-denied">
        <div className="w-full max-w-md text-center p-8">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">
            Access Denied
          </h1>
          <p className="text-[#737373] mb-8">
            Your account ({user.email}) does not have admin privileges.
          </p>
          <Button 
            asChild
            className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 px-8"
          >
            <Link to="/">Go to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center">
        <div className="animate-pulse text-[#737373]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-screen-2xl mx-auto px-6 py-6 flex justify-between items-center">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A]">Admin Dashboard</h1>
            <p className="text-sm text-[#737373]">Welcome, {user.email}</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-none gap-2"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 border border-[#E5E5E5]" data-testid="stat-products">
            <Package className="w-8 h-8 text-[#737373] mb-2" />
            <p className="text-2xl font-semibold text-[#0A0A0A]">{stats?.active_products ?? 0}</p>
            <p className="text-sm text-[#737373]">Active Products</p>
          </div>
          <div className="bg-white p-6 border border-[#E5E5E5]" data-testid="stat-orders">
            <ShoppingCart className="w-8 h-8 text-[#737373] mb-2" />
            <p className="text-2xl font-semibold text-[#0A0A0A]">{stats?.total_orders ?? 0}</p>
            <p className="text-sm text-[#737373]">Total Orders</p>
          </div>
          <div className="bg-white p-6 border border-[#E5E5E5]" data-testid="stat-pending">
            <ShoppingCart className="w-8 h-8 text-[#C9A962] mb-2" />
            <p className="text-2xl font-semibold text-[#0A0A0A]">{stats?.pending_orders ?? 0}</p>
            <p className="text-sm text-[#737373]">Pending Orders</p>
          </div>
          <div className="bg-white p-6 border border-[#E5E5E5]" data-testid="stat-revenue">
            <TrendingUp className="w-8 h-8 text-green-500 mb-2" />
            <p className="text-2xl font-semibold text-[#0A0A0A]">${(stats?.total_revenue ?? 0).toFixed(2)}</p>
            <p className="text-sm text-[#737373]">Revenue</p>
          </div>
        </div>

        {/* Low Stock Alerts */}
        {stats?.low_stock_count > 0 && (
          <div className="mb-8 p-4 bg-amber-50 border border-amber-200" data-testid="low-stock-alert">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-1">
                  Low Stock Alert: {stats.low_stock_count} product{stats.low_stock_count !== 1 ? 's' : ''} need attention
                </h3>
                <p className="text-sm text-amber-700 mb-3">
                  {stats.out_of_stock_count > 0 && (
                    <span className="font-medium text-red-600">{stats.out_of_stock_count} out of stock. </span>
                  )}
                  Products below {stats.low_stock_threshold} units in stock.
                </p>
                <div className="flex flex-wrap gap-2">
                  {stats.low_stock_products?.slice(0, 5).map((product) => (
                    <div 
                      key={product.id} 
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm ${
                        product.stock === 0 
                          ? 'bg-red-100 text-red-700 border border-red-200' 
                          : 'bg-white text-amber-700 border border-amber-200'
                      }`}
                    >
                      {product.image_url && (
                        <img src={product.image_url} alt="" className="w-6 h-6 object-cover" />
                      )}
                      <span className="font-medium">{product.name}</span>
                      <span className={`px-1.5 py-0.5 text-xs ${product.stock === 0 ? 'bg-red-200' : 'bg-amber-200'}`}>
                        {product.stock === 0 ? 'Out of stock' : `${product.stock} left`}
                      </span>
                    </div>
                  ))}
                  {stats.low_stock_products?.length > 5 && (
                    <span className="text-sm text-amber-600 self-center">
                      +{(stats.low_stock_products?.length ?? 0) - 5} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-white border border-[#E5E5E5] rounded-none p-1 flex-wrap">
            <TabsTrigger value="products" className="rounded-none" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="categories" className="rounded-none" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="orders" className="rounded-none" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="users" className="rounded-none" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="promotions" className="rounded-none" data-testid="tab-promotions">Promotions</TabsTrigger>
            <TabsTrigger value="discounts" className="rounded-none" data-testid="tab-discounts">Discounts</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-none" data-testid="tab-reviews">Reviews</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-xl font-semibold">Products ({products.length})</h2>
              <Button 
                className="rounded-none gap-2 bg-[#0A0A0A] hover:bg-[#262626]" 
                data-testid="add-product-btn"
                onClick={() => {
                  resetProductForm();
                  setProductDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Product
              </Button>
            </div>

            {/* Product Dialog */}
            <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
              <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleProductSubmit} className="space-y-6">
                  {/* Image Upload Section */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Product Image</Label>
                    <div className="flex gap-4">
                      {/* Image Preview */}
                      <div className="w-32 h-32 bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center overflow-hidden">
                        {imagePreview || productForm.image_url ? (
                          <img 
                            src={imagePreview || productForm.image_url} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-[#D4D4D4]" />
                        )}
                      </div>
                      
                      {/* Upload Controls */}
                      <div className="flex-1 space-y-3">
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handleImageUpload}
                              className="hidden"
                              data-testid="image-upload-input"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full rounded-none gap-2"
                              disabled={uploading}
                              onClick={(e) => e.currentTarget.previousElementSibling.click()}
                            >
                              <Upload className="w-4 h-4" />
                              {uploading ? 'Uploading...' : 'Upload Image'}
                            </Button>
                          </label>
                          {(imagePreview || productForm.image_url) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setImagePreview(null);
                                setProductForm({ ...productForm, image_url: '' });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-[#737373]">
                          JPG, PNG, WebP or GIF. Max 5MB.
                        </p>
                        <div className="relative">
                          <span className="text-xs text-[#737373]">Or paste image URL:</span>
                          <Input
                            value={productForm.image_url}
                            onChange={(e) => {
                              setProductForm({ ...productForm, image_url: e.target.value });
                              setImagePreview(e.target.value);
                            }}
                            placeholder="https://..."
                            className="mt-1 rounded-none text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Product Name *</Label>
                      <Input
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        required
                        className="rounded-none mt-1"
                        placeholder="Executive Leather Diary"
                        data-testid="product-name-input"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Description *</Label>
                      <Textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        required
                        className="rounded-none mt-1 min-h-[80px]"
                        placeholder="Describe your product..."
                      />
                    </div>
                    
                    <div>
                      <Label>Price *</Label>
                      <div className="flex gap-2 mt-1">
                        <Select
                          value={productForm.currency}
                          onValueChange={(v) => setProductForm({ ...productForm, currency: v })}
                        >
                          <SelectTrigger className="rounded-none w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            <SelectItem value="USD">$ USD</SelectItem>
                            <SelectItem value="EUR">€ EUR</SelectItem>
                            <SelectItem value="GBP">£ GBP</SelectItem>
                            <SelectItem value="ZAR">R ZAR</SelectItem>
                            <SelectItem value="NGN">₦ NGN</SelectItem>
                            <SelectItem value="KES">KSh KES</SelectItem>
                            <SelectItem value="GHS">₵ GHS</SelectItem>
                            <SelectItem value="BWP">P BWP</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={productForm.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                          required
                          className="rounded-none flex-1"
                          placeholder="99.99"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Stock Quantity *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={productForm.stock}
                        onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                        required
                        className="rounded-none mt-1"
                        placeholder="50"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Category *</Label>
                      <Select
                        value={productForm.category}
                        onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                      >
                        <SelectTrigger className="rounded-none mt-1">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-[#E5E5E5]">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={productForm.featured}
                          onCheckedChange={(v) => setProductForm({ ...productForm, featured: v })}
                          id="featured"
                        />
                        <Label htmlFor="featured" className="cursor-pointer">Featured Product</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={productForm.is_active}
                          onCheckedChange={(v) => setProductForm({ ...productForm, is_active: v })}
                          id="active"
                        />
                        <Label htmlFor="active" className="cursor-pointer">Active</Label>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-none"
                      onClick={() => setProductDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 rounded-none bg-[#0A0A0A] hover:bg-[#262626] gap-2"
                      disabled={!productForm.name || !productForm.price || !productForm.category || !productForm.image_url}
                    >
                      <Save className="w-4 h-4" />
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <div 
                  key={product.id} 
                  className="bg-white border border-[#E5E5E5] overflow-hidden group"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="aspect-square relative overflow-hidden bg-[#F5F5F5]">
                    <img 
                      src={product.image_url} 
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                    {!product.is_active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">Hidden</span>
                      </div>
                    )}
                    {product.featured && (
                      <div className="absolute top-2 left-2 bg-[#C9A962] text-white text-xs px-2 py-1">
                        Featured
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="w-8 h-8 rounded-none"
                        onClick={() => handleEditProduct(product)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="destructive"
                        className="w-8 h-8 rounded-none"
                        onClick={() => handleDeleteProduct(product.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-[#0A0A0A] truncate">{product.name}</h3>
                    <p className="text-sm text-[#737373] capitalize">{product.category.replace('-', ' ')}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-semibold text-[#0A0A0A]">
                        {product.currency === 'EUR' ? '€' : 
                         product.currency === 'GBP' ? '£' : 
                         product.currency === 'ZAR' ? 'R' : 
                         product.currency === 'NGN' ? '₦' : 
                         product.currency === 'KES' ? 'KSh' : 
                         product.currency === 'GHS' ? '₵' : 
                         product.currency === 'BWP' ? 'P' : '$'}
                        {product.price.toFixed(2)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#737373]">Stock:</span>
                        <Input
                          type="number"
                          defaultValue={product.stock}
                          onBlur={(e) => {
                            if (parseInt(e.target.value) !== product.stock) {
                              handleUpdateStock(product.id, e.target.value);
                            }
                          }}
                          className="w-16 h-7 rounded-none text-sm text-center"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-xl font-semibold">Categories ({categories.length})</h2>
              <Button 
                className="rounded-none gap-2 bg-[#0A0A0A] hover:bg-[#262626]" 
                data-testid="add-category-btn"
                onClick={() => {
                  resetCategoryForm();
                  setCategoryDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Category
              </Button>
            </div>

            {/* Category Dialog */}
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingCategory ? 'Update the category details below.' : 'Create a new product category.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCategorySubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Category Name *</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => handleCategoryNameChange(e.target.value)}
                      placeholder="e.g., Business Cards"
                      className="rounded-none"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Slug *</Label>
                    <Input
                      value={categoryForm.slug}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="e.g., business-cards"
                      className="rounded-none"
                      required
                    />
                    <p className="text-xs text-[#737373]">URL-friendly identifier. Auto-generated from name.</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the category..."
                      className="rounded-none min-h-[80px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Category Image (Optional)</Label>
                    <div className="flex gap-4 items-start">
                      <div className="flex-1">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#E5E5E5] cursor-pointer hover:border-[#C9A962] transition-colors">
                          <div className="flex flex-col items-center justify-center py-4">
                            {categoryImageUploading ? (
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A0A0A]"></div>
                            ) : (
                              <>
                                <Upload className="w-8 h-8 text-[#737373] mb-2" />
                                <p className="text-sm text-[#737373]">Click to upload</p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleCategoryImageUpload}
                            disabled={categoryImageUploading}
                          />
                        </label>
                      </div>
                      {categoryImagePreview && (
                        <div className="relative w-32 h-32 border border-[#E5E5E5]">
                          <img 
                            src={categoryImagePreview} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setCategoryImagePreview(null);
                              setCategoryForm(prev => ({ ...prev, image_url: '' }));
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none"
                      onClick={() => {
                        setCategoryDialogOpen(false);
                        resetCategoryForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="rounded-none bg-[#0A0A0A] hover:bg-[#262626]"
                      disabled={!categoryForm.name || !categoryForm.slug}
                    >
                      {editingCategory ? 'Update Category' : 'Create Category'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Categories Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {categories.length === 0 ? (
                <div className="col-span-full p-12 text-center text-[#737373] bg-white border border-[#E5E5E5]">
                  <FolderOpen className="w-12 h-12 mx-auto mb-4 text-[#D4D4D4]" />
                  <p>No categories yet. Add your first category to organize products.</p>
                </div>
              ) : (
                categories.map((category) => (
                  <div key={category.id} className="bg-white border border-[#E5E5E5] overflow-hidden">
                    {category.image_url && (
                      <div className="aspect-video bg-[#F5F5F5] overflow-hidden">
                        <img 
                          src={category.image_url} 
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-[#0A0A0A]">{category.name}</h3>
                          <p className="text-xs text-[#737373] font-mono">{category.slug}</p>
                        </div>
                        <span className="px-2 py-1 text-xs bg-[#F5F5F5] text-[#737373]">
                          {getProductCountForCategory(category.slug)} products
                        </span>
                      </div>
                      {category.description && (
                        <p className="text-sm text-[#737373] mb-4 line-clamp-2">{category.description}</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none flex-1"
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteCategory(category.id)}
                          disabled={getProductCountForCategory(category.slug) > 0}
                          title={getProductCountForCategory(category.slug) > 0 ? 'Reassign products first' : 'Delete category'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {getProductCountForCategory(category.slug) > 0 && (
                        <p className="text-xs text-amber-600 mt-2">
                          * Cannot delete - has {getProductCountForCategory(category.slug)} product(s)
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <h2 className="font-serif text-xl font-semibold">Orders ({orders.length})</h2>
            
            <div className="bg-white border border-[#E5E5E5] overflow-hidden overflow-x-auto">
              {orders.length === 0 ? (
                <div className="p-8 text-center text-[#737373]">No orders yet</div>
              ) : (
                <table className="w-full min-w-[800px]">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Order #</th>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Customer</th>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Items</th>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Total</th>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Status</th>
                      <th className="text-right p-4 text-sm font-medium text-[#737373]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className="border-b border-[#E5E5E5] last:border-0" data-testid={`order-row-${order.id}`}>
                        <td className="p-4 font-medium text-[#0A0A0A]">{order.order_number}</td>
                        <td className="p-4">
                          <div>
                            <p className="text-sm text-[#0A0A0A]">{order.customer_info?.name}</p>
                            <p className="text-xs text-[#737373]">{order.customer_info?.email}</p>
                            <p className="text-xs text-[#737373]">{order.customer_info?.phone}</p>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-[#737373]">{order.items?.length || 0} items</td>
                        <td className="p-4 text-sm font-medium text-[#0A0A0A]">${order.total?.toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`inline-block px-2 py-1 text-xs capitalize ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                            order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            order.status === 'confirmed' ? 'bg-purple-100 text-purple-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedOrder(order);
                                setOrderDialogOpen(true);
                              }}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Select
                              value={order.status}
                              onValueChange={(v) => handleOrderStatus(order.id, v)}
                            >
                              <SelectTrigger className="w-32 h-8 rounded-none text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="rounded-none">
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="processing">Processing</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Order Details Dialog */}
            <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
              <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    Order Details - {selectedOrder?.order_number}
                  </DialogTitle>
                </DialogHeader>
                {selectedOrder && (
                  <div className="space-y-6">
                    {/* Order Status */}
                    <div className="flex items-center justify-between pb-4 border-b">
                      <div>
                        <p className="text-sm text-[#737373]">Order Date</p>
                        <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                      </div>
                      <span className={`px-3 py-1 text-sm capitalize ${
                        selectedOrder.status === 'delivered' ? 'bg-green-100 text-green-700' :
                        selectedOrder.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                        selectedOrder.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        selectedOrder.status === 'confirmed' ? 'bg-purple-100 text-purple-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selectedOrder.status}
                      </span>
                    </div>

                    {/* Customer Information */}
                    <div>
                      <h3 className="font-semibold mb-3">Customer Information</h3>
                      <div className="bg-[#FAFAFA] p-4 space-y-2">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-[#737373]">Name</p>
                            <p className="font-medium">{selectedOrder.customer_info?.name || '—'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-[#737373]">Email</p>
                            <p className="font-medium">{selectedOrder.customer_info?.email || '—'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-[#737373]">Phone</p>
                            <p className="font-medium">{selectedOrder.customer_info?.phone || '—'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-[#737373]">Company</p>
                            <p className="font-medium">{selectedOrder.customer_info?.company || '—'}</p>
                          </div>
                        </div>
                        <div className="pt-2">
                          <p className="text-sm text-[#737373]">Address</p>
                          <p className="font-medium">
                            {selectedOrder.customer_info?.address || '—'}
                            {selectedOrder.customer_info?.city && `, ${selectedOrder.customer_info.city}`}
                            {selectedOrder.customer_info?.state && `, ${selectedOrder.customer_info.state}`}
                            {selectedOrder.customer_info?.postal_code && ` ${selectedOrder.customer_info.postal_code}`}
                            {selectedOrder.customer_info?.country && `, ${selectedOrder.customer_info.country}`}
                          </p>
                        </div>
                        {selectedOrder.customer_info?.notes && (
                          <div className="pt-2">
                            <p className="text-sm text-[#737373]">Notes</p>
                            <p className="font-medium">{selectedOrder.customer_info.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order Items */}
                    <div>
                      <h3 className="font-semibold mb-3">Order Items ({selectedOrder.items?.length || 0})</h3>
                      <div className="border divide-y">
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-4">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover" />
                            ) : (
                              <div className="w-16 h-16 bg-[#F5F5F5] flex items-center justify-center">
                                <Package className="w-6 h-6 text-[#D4D4D4]" />
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium">{item.name}</p>
                              <p className="text-sm text-[#737373]">Qty: {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                              <p className="text-sm text-[#737373]">${item.price?.toFixed(2)} each</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div>
                      <h3 className="font-semibold mb-3">Order Summary</h3>
                      <div className="bg-[#FAFAFA] p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-[#737373]">Subtotal</span>
                          <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                        </div>
                        {selectedOrder.discount_code && (
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({selectedOrder.discount_code})</span>
                            <span>-${selectedOrder.discount_amount?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                          <span>Total</span>
                          <span>${selectedOrder.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Update Status */}
                    <div className="flex items-center gap-4 pt-4 border-t">
                      <Label>Update Status:</Label>
                      <Select
                        value={selectedOrder.status}
                        onValueChange={(v) => {
                          handleOrderStatus(selectedOrder.id, v);
                          setSelectedOrder({ ...selectedOrder, status: v });
                        }}
                      >
                        <SelectTrigger className="w-40 rounded-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-xl font-semibold">Promotions ({promotions.length})</h2>
              <Button 
                className="rounded-none gap-2 bg-[#0A0A0A] hover:bg-[#262626]" 
                onClick={() => {
                  resetPromotionForm();
                  setPromotionDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Promotion
              </Button>
            </div>

            {/* Promotion Dialog */}
            <Dialog open={promotionDialogOpen} onOpenChange={setPromotionDialogOpen}>
              <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {editingPromotion ? 'Edit Promotion' : 'Add New Promotion'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePromotionSubmit} className="space-y-6">
                  {/* Image Section */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Promotion Banner *</Label>
                    <div className="flex gap-4">
                      <div className="w-48 h-28 bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center overflow-hidden relative group">
                        {promotionImagePreview || promotionForm.image_url ? (
                          <>
                            <img 
                              src={promotionImagePreview || promotionForm.image_url} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/gif"
                                  onChange={handlePromotionImageUpload}
                                  className="hidden"
                                  disabled={promotionUploading}
                                />
                                <span className="text-white text-xs flex items-center gap-1">
                                  <Upload className="w-3 h-3" />
                                  Change
                                </span>
                              </label>
                            </div>
                          </>
                        ) : (
                          <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full hover:bg-[#EFEFEF] transition-colors">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handlePromotionImageUpload}
                              className="hidden"
                              disabled={promotionUploading}
                            />
                            {promotionUploading ? (
                              <div className="animate-spin w-6 h-6 border-2 border-[#C9A962] border-t-transparent rounded-full" />
                            ) : (
                              <>
                                <Upload className="w-6 h-6 text-[#737373] mb-1" />
                                <span className="text-xs text-[#737373]">Upload</span>
                              </>
                            )}
                          </label>
                        )}
                        {promotionUploading && (promotionImagePreview || promotionForm.image_url) && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={promotionForm.image_url}
                            onChange={(e) => {
                              setPromotionForm({ ...promotionForm, image_url: e.target.value });
                              setPromotionImagePreview(e.target.value);
                            }}
                            placeholder="https://images.unsplash.com/..."
                            className="rounded-none text-sm flex-1"
                            required
                          />
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handlePromotionImageUpload}
                              className="hidden"
                              disabled={promotionUploading}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-none h-9 px-3"
                              disabled={promotionUploading}
                              asChild
                            >
                              <span>
                                {promotionUploading ? (
                                  <div className="animate-spin w-4 h-4 border-2 border-[#0A0A0A] border-t-transparent rounded-full" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                              </span>
                            </Button>
                          </label>
                        </div>
                        <p className="text-xs text-[#737373]">
                          Upload an image or paste a URL. Recommended: 1200x600px (2:1 aspect ratio)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Title *</Label>
                      <Input
                        value={promotionForm.title}
                        onChange={(e) => setPromotionForm({ ...promotionForm, title: e.target.value })}
                        required
                        className="rounded-none mt-1"
                        placeholder="Summer Sale 2026"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Subtitle</Label>
                      <Input
                        value={promotionForm.subtitle}
                        onChange={(e) => setPromotionForm({ ...promotionForm, subtitle: e.target.value })}
                        className="rounded-none mt-1"
                        placeholder="Up to 50% Off"
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Description *</Label>
                      <Textarea
                        value={promotionForm.description}
                        onChange={(e) => setPromotionForm({ ...promotionForm, description: e.target.value })}
                        required
                        className="rounded-none mt-1 min-h-[80px]"
                        placeholder="Get amazing discounts on premium branding products..."
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Link URL</Label>
                      <Input
                        value={promotionForm.link_url}
                        onChange={(e) => setPromotionForm({ ...promotionForm, link_url: e.target.value })}
                        className="rounded-none mt-1"
                        placeholder="/store?category=business-cards"
                      />
                      <p className="text-xs text-[#737373] mt-1">Where users go when they click the promotion</p>
                    </div>

                    <div className="col-span-2 flex items-center justify-between p-4 bg-[#FAFAFA] border border-[#E5E5E5]">
                      <div>
                        <Label>Active Status</Label>
                        <p className="text-xs text-[#737373]">Show this promotion on the website</p>
                      </div>
                      <Switch
                        checked={promotionForm.is_active}
                        onCheckedChange={(checked) => setPromotionForm({ ...promotionForm, is_active: checked })}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none"
                      onClick={() => {
                        resetPromotionForm();
                        setPromotionDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] gap-2">
                      <Save className="w-4 h-4" />
                      {editingPromotion ? 'Update Promotion' : 'Create Promotion'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {promotions.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E5E5E5]">
                <Megaphone className="w-12 h-12 text-[#D4D4D4] mx-auto mb-4" />
                <p className="text-[#737373] mb-4">No promotions yet</p>
                <Button 
                  variant="outline" 
                  className="rounded-none"
                  onClick={() => {
                    resetPromotionForm();
                    setPromotionDialogOpen(true);
                  }}
                >
                  Create Your First Promotion
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {promotions.map((promo) => (
                  <div key={promo.id} className="bg-white border border-[#E5E5E5] overflow-hidden group" data-testid={`promo-card-${promo.id}`}>
                    <div className="aspect-video relative">
                      <img src={promo.image_url} alt={promo.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="rounded-none"
                          onClick={() => handleEditPromotion(promo)}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="rounded-none text-red-600"
                          onClick={() => handleDeletePromotion(promo.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                      <div className="absolute top-2 right-2">
                        <button
                          onClick={() => handleTogglePromotionStatus(promo)}
                          className={`px-2 py-1 text-xs cursor-pointer hover:opacity-80 ${promo.is_active ? 'bg-green-100 text-green-700' : 'bg-[#F5F5F5] text-[#737373]'}`}
                        >
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-[#0A0A0A]">{promo.title}</h3>
                          <p className="text-sm text-[#C9A962]">{promo.subtitle}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleEditPromotion(promo)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => handleDeletePromotion(promo.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-[#737373] mt-1 line-clamp-2">{promo.description}</p>
                      {promo.link_url && (
                        <p className="text-xs text-[#737373] mt-2">
                          Links to: <span className="text-[#0A0A0A]">{promo.link_url}</span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-xl font-semibold">Discount Codes ({discounts.length})</h2>
              <Button 
                className="rounded-none gap-2 bg-[#0A0A0A] hover:bg-[#262626]" 
                onClick={() => {
                  resetDiscountForm();
                  setDiscountDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Discount
              </Button>
            </div>

            {/* Discount Dialog */}
            <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
              <DialogContent className="max-w-lg rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {editingDiscount ? 'Edit Discount' : 'Create Discount Code'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleDiscountSubmit} className="space-y-4">
                  <div>
                    <Label>Code *</Label>
                    <Input
                      value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      required
                      className="rounded-none mt-1 uppercase"
                      placeholder="SAVE20"
                    />
                    <p className="text-xs text-[#737373] mt-1">Customers will enter this code at checkout</p>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Input
                      value={discountForm.description}
                      onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                      className="rounded-none mt-1"
                      placeholder="20% off all orders"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type *</Label>
                      <Select
                        value={discountForm.discount_type}
                        onValueChange={(v) => setDiscountForm({ ...discountForm, discount_type: v })}
                      >
                        <SelectTrigger className="rounded-none mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Value *</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={discountForm.discount_value}
                          onChange={(e) => setDiscountForm({ ...discountForm, discount_value: e.target.value })}
                          required
                          className="rounded-none pl-8"
                          placeholder={discountForm.discount_type === 'percentage' ? '20' : '10.00'}
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]">
                          {discountForm.discount_type === 'percentage' ? '%' : '$'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Minimum Order Amount</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={discountForm.min_order_amount}
                          onChange={(e) => setDiscountForm({ ...discountForm, min_order_amount: e.target.value })}
                          className="rounded-none pl-8"
                          placeholder="50.00"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]">$</span>
                      </div>
                    </div>
                    <div>
                      <Label>Max Uses</Label>
                      <Input
                        type="number"
                        min="1"
                        value={discountForm.max_uses}
                        onChange={(e) => setDiscountForm({ ...discountForm, max_uses: e.target.value })}
                        className="rounded-none mt-1"
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Expiration Date</Label>
                    <Input
                      type="date"
                      value={discountForm.expires_at}
                      onChange={(e) => setDiscountForm({ ...discountForm, expires_at: e.target.value })}
                      className="rounded-none mt-1"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-[#FAFAFA] border border-[#E5E5E5]">
                    <div>
                      <Label>Active</Label>
                      <p className="text-xs text-[#737373]">Customers can use this code</p>
                    </div>
                    <Switch
                      checked={discountForm.is_active}
                      onCheckedChange={(checked) => setDiscountForm({ ...discountForm, is_active: checked })}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E5E5]">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none"
                      onClick={() => {
                        resetDiscountForm();
                        setDiscountDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="rounded-none bg-[#0A0A0A] hover:bg-[#262626] gap-2">
                      <Save className="w-4 h-4" />
                      {editingDiscount ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {discounts.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E5E5E5]">
                <Ticket className="w-12 h-12 text-[#D4D4D4] mx-auto mb-4" />
                <p className="text-[#737373] mb-4">No discount codes yet</p>
                <Button 
                  variant="outline" 
                  className="rounded-none"
                  onClick={() => {
                    resetDiscountForm();
                    setDiscountDialogOpen(true);
                  }}
                >
                  Create Your First Discount
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-[#E5E5E5] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#737373]">Code</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#737373]">Discount</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#737373]">Min. Order</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#737373]">Usage</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#737373]">Expires</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#737373]">Status</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-[#737373]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {discounts.map((discount) => {
                      const isExpired = discount.expires_at && new Date(discount.expires_at) < new Date();
                      const isMaxed = discount.max_uses && discount.times_used >= discount.max_uses;
                      
                      return (
                        <tr key={discount.id} className="hover:bg-[#FAFAFA]">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Ticket className="w-4 h-4 text-[#C9A962]" />
                              <span className="font-mono font-semibold text-[#0A0A0A]">{discount.code}</span>
                            </div>
                            {discount.description && (
                              <p className="text-xs text-[#737373] mt-1">{discount.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {discount.discount_type === 'percentage' ? (
                                <>
                                  <Percent className="w-4 h-4 text-[#737373]" />
                                  <span>{discount.discount_value}%</span>
                                </>
                              ) : (
                                <>
                                  <DollarSign className="w-4 h-4 text-[#737373]" />
                                  <span>${discount.discount_value.toFixed(2)}</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#737373]">
                            {discount.min_order_amount ? `$${discount.min_order_amount.toFixed(2)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={isMaxed ? 'text-red-600' : 'text-[#737373]'}>
                              {discount.times_used}{discount.max_uses ? ` / ${discount.max_uses}` : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {discount.expires_at ? (
                              <span className={isExpired ? 'text-red-600' : 'text-[#737373]'}>
                                {new Date(discount.expires_at).toLocaleDateString()}
                                {isExpired && ' (Expired)'}
                              </span>
                            ) : (
                              <span className="text-[#737373]">Never</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleToggleDiscountStatus(discount)}
                              className={`px-2 py-1 text-xs cursor-pointer hover:opacity-80 ${
                                discount.is_active && !isExpired && !isMaxed
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-[#F5F5F5] text-[#737373]'
                              }`}
                            >
                              {discount.is_active && !isExpired && !isMaxed ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleEditDiscount(discount)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteDiscount(discount.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-6" data-testid="reviews-tab-content">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-xl font-semibold">Product Reviews ({reviews.length})</h2>
              <div className="flex items-center gap-4 text-sm text-[#737373]">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{reviews.filter(r => !r.is_approved).length} pending</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>{reviews.filter(r => r.is_approved).length} approved</span>
                </div>
              </div>
            </div>

            {reviews.length === 0 ? (
              <div className="bg-white p-8 text-center border">
                <MessageSquare className="w-12 h-12 text-[#D4D4D4] mx-auto mb-4" />
                <p className="text-[#737373]">No reviews yet</p>
              </div>
            ) : (
              <div className="bg-white border overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAFA] border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] uppercase">Rating</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] uppercase">Review</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] uppercase">Author</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-[#737373] uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {reviews.map(review => (
                      <tr key={review.id} className="hover:bg-[#FAFAFA]">
                        <td className="px-4 py-3 text-sm">
                          <span className="font-medium">{getProductName(review.product_id)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'fill-[#C9A962] text-[#C9A962]' : 'text-[#E5E5E5]'}`} 
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          {review.title && (
                            <p className="font-medium text-sm">{review.title}</p>
                          )}
                          <p className="text-sm text-[#737373] truncate">{review.comment}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium">{review.author_name}</p>
                            <p className="text-xs text-[#737373]">{review.author_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#737373]">
                          {new Date(review.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs ${
                            review.is_approved 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {review.is_approved ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            {!review.is_approved && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-green-600 hover:text-green-700"
                                onClick={() => handleApproveReview(review.id)}
                                title="Approve"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {review.is_approved && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-yellow-600 hover:text-yellow-700"
                                onClick={() => handleRejectReview(review.id)}
                                title="Unapprove"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-600"
                              onClick={() => handleDeleteReview(review.id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-serif text-xl font-semibold">Users ({users.length})</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-[#737373]">
                  <Users className="w-4 h-4" />
                  <span>{users.filter(u => u.role === 'admin').length} admins</span>
                </div>
                <Button 
                  className="rounded-none gap-2 bg-[#C9A962] hover:bg-[#B8994D] text-white"
                  onClick={() => {
                    setCreatedInviteLink('');
                    setNewInviteEmail('');
                    setInviteDialogOpen(true);
                  }}
                >
                  <Mail className="w-4 h-4" />
                  Invite Admin
                </Button>
              </div>
            </div>

            {/* Invite Dialog */}
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogContent className="max-w-md rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">Invite Admin</DialogTitle>
                  <DialogDescription className="text-[#737373]">
                    Create an invite link to add a new admin user
                  </DialogDescription>
                </DialogHeader>
                
                {createdInviteLink ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200">
                      <p className="text-sm text-green-700 font-medium mb-2">Invite link created!</p>
                      <p className="text-xs text-green-600 mb-3">Share this link with the user. It expires in 7 days.</p>
                      <div className="flex gap-2">
                        <Input
                          value={createdInviteLink}
                          readOnly
                          className="rounded-none text-xs flex-1"
                        />
                        <Button
                          variant="outline"
                          className="rounded-none"
                          onClick={() => {
                            navigator.clipboard.writeText(createdInviteLink);
                            toast.success('Link copied!');
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full rounded-none"
                      onClick={() => {
                        setCreatedInviteLink('');
                        setInviteDialogOpen(false);
                      }}
                    >
                      Done
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-[#737373]">Email Address (Optional)</Label>
                      <Input
                        type="email"
                        value={newInviteEmail}
                        onChange={(e) => setNewInviteEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="mt-2 rounded-none"
                      />
                      <p className="text-xs text-[#737373] mt-1">
                        Leave empty to create a generic invite link
                      </p>
                    </div>
                    <Button
                      className="w-full rounded-none bg-[#0A0A0A] hover:bg-[#262626]"
                      onClick={handleCreateInvite}
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Generate Invite Link
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Pending Invites */}
            {invites.filter(i => !i.used).length > 0 && (
              <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4">
                <h3 className="text-sm font-medium text-[#0A0A0A] mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#C9A962]" />
                  Pending Invites ({invites.filter(i => !i.used).length})
                </h3>
                <div className="space-y-2">
                  {invites.filter(i => !i.used).map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between bg-white p-3 border border-[#E5E5E5]">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#C9A962]/10 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-[#C9A962]" />
                        </div>
                        <div>
                          <p className="text-sm text-[#0A0A0A]">
                            {invite.email || 'Generic invite link'}
                          </p>
                          <p className="text-xs text-[#737373]">
                            Expires: {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-none text-xs"
                          onClick={() => handleCopyInviteLink(invite.token)}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-none text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            if (window.confirm('Revoke this invite?')) {
                              handleRevokeInvite(invite.id);
                            }
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="bg-white border border-[#E5E5E5] overflow-hidden overflow-x-auto">
              {users.length === 0 ? (
                <div className="p-8 text-center text-[#737373]">No users yet</div>
              ) : (
                <table className="w-full min-w-[600px]">
                  <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">User</th>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Role</th>
                      <th className="text-left p-4 text-sm font-medium text-[#737373]">Joined</th>
                      <th className="text-right p-4 text-sm font-medium text-[#737373]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-[#E5E5E5] last:border-0" data-testid={`user-row-${u.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#0A0A0A] rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-medium">
                                {u.email?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#0A0A0A]">{u.name || 'No name'}</p>
                              <p className="text-xs text-[#737373]">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${
                            u.role === 'admin' 
                              ? 'bg-[#C9A962]/10 text-[#C9A962]' 
                              : 'bg-[#F5F5F5] text-[#737373]'
                          }`}>
                            {u.role === 'admin' ? (
                              <Shield className="w-3 h-3" />
                            ) : null}
                            {u.role === 'admin' ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-[#737373]">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-4 text-right">
                          {u.email === user.email ? (
                            <span className="text-xs text-[#737373] italic">You</span>
                          ) : u.role === 'admin' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                if (window.confirm(`Remove admin privileges from ${u.email}?`)) {
                                  handleUserRoleUpdate(u.id, 'user');
                                }
                              }}
                            >
                              <UserX className="w-3 h-3" />
                              Demote
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none text-xs gap-1 text-[#C9A962] border-[#C9A962]/30 hover:bg-[#C9A962]/5"
                              onClick={() => {
                                if (window.confirm(`Promote ${u.email} to admin?`)) {
                                  handleUserRoleUpdate(u.id, 'admin');
                                }
                              }}
                            >
                              <UserCheck className="w-3 h-3" />
                              Promote to Admin
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
